import { createGuildClient } from "@guildxyz/sdk";
import * as Sentry from "@sentry/browser";
import { compact } from "lodash-es";
import { createAsyncAction, errorResult, successResult } from "pullstate";
import { getIsUserConnected } from "utils/userUtils";
import { Address, isAddress } from "viem";
import {
  BEBOP_GUILD_ID,
  GUILD_NAMES_TO_ID,
  GUILD_ROLES_PRECEDENCE,
  PROJECT_NAME,
} from "../../config/consts";
import { isLocal } from "../../config/env";
import { GlobalStore } from "./GlobalStore";

const {
  guild: { role: roleClient },
  user: userClient,
} = createGuildClient(PROJECT_NAME);

export interface Role {
  id: number;
  precedence: number;
  name: string;
}

export const fetchRoles = async (userAddress: Address): Promise<Array<Role>> => {
  if (!isAddress(userAddress)) return [];

  let userRoleIds: Awaited<ReturnType<typeof userClient.getMemberships>>[0]["roleIds"] = [];
  let allRoles: Awaited<ReturnType<typeof roleClient.getAll>> = [];

  try {
    const membershipResult = (await userClient.getMemberships(userAddress)).find(
      value => value.guildId === BEBOP_GUILD_ID
    );
    if (membershipResult == null) return [];

    userRoleIds = membershipResult.roleIds.filter(id =>
      Object.values(GUILD_NAMES_TO_ID).includes(id)
    );
  } catch (error) {
    console.log(`Could not get user's guild role IDs`, error);
    Sentry.captureException(error);
  }

  try {
    allRoles = await roleClient.getAll(BEBOP_GUILD_ID);
  } catch (error) {
    console.log(`Could not get all Bebop guild roles' data`, error);
    Sentry.captureException(error);
  }

  const roles = compact(
    userRoleIds.map(roleId => allRoles.find(role => role["id"] === roleId))
  ).map(role => {
    return {
      id: role.id,
      name: role.name,
      precedence: GUILD_ROLES_PRECEDENCE[role.id],
    } as Role;
  });

  return roles;
};

export const getRoles = createAsyncAction(
  async ({ userAddress }: { userAddress: `0x${string}` }) => {
    try {
      const result = await fetchRoles(userAddress);
      if (result) {
        if (isLocal) {
          result.push({
            id: GUILD_NAMES_TO_ID.DEV_MOCK,
            name: "Beep boop Bebop",
            precedence: GUILD_ROLES_PRECEDENCE[GUILD_NAMES_TO_ID.DEV_MOCK],
          });
        }
        // Store in GlobalStore in precedence to prevent need for sorting every time a guild role is retrieved
        return successResult(result.sort((roleA, roleB) => roleA.precedence - roleB.precedence));
      }
    } catch (e) {
      console.log(`Could not get guild roles`, e);
      return errorResult([], `Could not get guild roles`, e);
    }
    return errorResult([], `Could not get guild roles`);
  },
  {
    postActionHook: ({ result }) => {
      if (!result.error && getIsUserConnected()) {
        GlobalStore.update(s => {
          s.userRoles = result.payload;
        });
      }
    },
  }
);
